import React, { useState } from 'react'
import { Button } from '@vibe/core'
import type { Candidate } from '../../types'
import {
  QualificationStatus,
  UserRole,
  PermissionResource,
  PermissionAction,
} from '../../types'
import { PermissionGate } from './PermissionGate'

export interface CandidateQualificationActionsProps {
  candidate: Candidate
  onQualificationUpdate: (
    candidateId: string,
    status: QualificationStatus
  ) => Promise<void>
  loading?: boolean
  className?: string
}

const CandidateQualificationActions: React.FC<
  CandidateQualificationActionsProps
> = ({ candidate, onQualificationUpdate, loading = false, className = '' }) => {
  const [isUpdating, setIsUpdating] = useState(false)
  const [lastAction, setLastAction] = useState<string | null>(null)

  const handleQualificationClick = async (status: QualificationStatus) => {
    if (isUpdating || loading) return

    try {
      setIsUpdating(true)
      setLastAction(status)
      await onQualificationUpdate(candidate.id_postulacion, status)
    } catch (error) {
      console.error('Error updating qualification:', error)
      // Error handling will be managed by parent component
    } finally {
      setIsUpdating(false)
      setLastAction(null)
    }
  }

  const isButtonLoading = (status: QualificationStatus) => {
    return (loading || isUpdating) && lastAction === status
  }

  const isButtonDisabled = () => {
    return loading || isUpdating
  }

  return (
    <PermissionGate
      requiredRoles={[UserRole.ADMINISTRADOR, UserRole.EDITOR]}
      requireAll={false}
      fallback={
        <div className="text-sm text-gray-500 italic">
          No tienes permisos para calificar candidatos
        </div>
      }
    >
      <div
        className={`space-y-4 ${className}`}
        data-testid="qualification-actions"
      >
        <div className="text-sm text-gray-700 font-medium">
          Luego de haber leído la evaluación generada por IA y la información
          del postulante, califícalo
        </div>

        <div className="flex gap-3">
          <Button
            onClick={() =>
              handleQualificationClick(QualificationStatus.CALIFICA)
            }
            disabled={isButtonDisabled()}
            loading={isButtonLoading(QualificationStatus.CALIFICA)}
            kind="primary"
            size="medium"
            data-testid="califica-button"
          >
            Califica
          </Button>

          <Button
            onClick={() =>
              handleQualificationClick(QualificationStatus.NO_CALIFICA)
            }
            disabled={isButtonDisabled()}
            loading={isButtonLoading(QualificationStatus.NO_CALIFICA)}
            kind="secondary"
            size="medium"
            data-testid="no-califica-button"
          >
            No califica
          </Button>
        </div>

        {candidate.qualification_status !== QualificationStatus.PENDIENTE && (
          <div className="text-xs text-gray-500">
            {candidate.qualified_by && candidate.qualified_at && (
              <span>
                Calificado por {candidate.qualified_by} el{' '}
                {new Date(candidate.qualified_at).toLocaleDateString('es-ES', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            )}
          </div>
        )}
      </div>
    </PermissionGate>
  )
}

export default CandidateQualificationActions
